/**
 * Staff API service
 * Handles all staff-related API calls
 */

import { apiFetch } from './index';

export interface Staff {
  staffId: number;
  personId: number;
  staffClassificationDescriptorId?: number;
  hireDate?: string;
  isActive: boolean;
  person: {
    personId: number;
    firstName: string;
    middleName?: string;
    lastSurname: string;
    preferredName?: string;
    emails?: Array<{
      emailAddress: string;
      isPrimary?: boolean;
    }>;
    phones?: Array<{
      phoneNumber: string;
      isPrimary?: boolean;
    }>;
  };
  assignments: StaffAssignment[];
  hasUserAccount: boolean;
  userAccountId?: number;
  createdAt: string;
  modifiedAt?: string;
}

export interface StaffAssignment {
  assignmentId: number;
  educationOrganizationId: number;
  organizationName: string;
  positionTitle?: string;
  roleDescriptorId?: number;
  roleName?: string;
  beginDate?: string;
  endDate?: string;
  isPrimary: boolean;
  includeInDirectory: boolean;
  // A school's district; tags saved there count for the school position
  // when the person has no district position
  districtEducationOrganizationId?: number | null;
}

export interface StaffCreateData {
  personId: number;
  staffClassificationDescriptorId?: number;
  hireDate?: string;
  isActive?: boolean;
}

export interface StaffUpdateData {
  staffClassificationDescriptorId?: number;
  hireDate?: string;
  isActive?: boolean;
}

export interface StaffAssignmentCreateData {
  educationOrganizationId: number;
  positionTitle?: string;
  roleDescriptorId?: number;
  beginDate?: string;
  endDate?: string;
  isPrimary?: boolean;
  includeInDirectory?: boolean;
  // Staff Role descriptors saved as role tags in the same request
  roleTagDescriptorIds?: number[];
}

export interface RoleTagSuggestion {
  descriptorId: number;
  codeValue: string;
  shortDescription: string;
}

export interface StaffAssignmentUpdateData {
  positionTitle?: string;
  roleDescriptorId?: number;
  beginDate?: string;
  endDate?: string;
  isPrimary?: boolean;
  includeInDirectory?: boolean;
  // For a position: the full set of tags its organization should have,
  // saved with the update. Omit to leave tags alone.
  roleTagDescriptorIds?: number[];
}

export interface BulkEnableLoginRequest {
  staffIds: number[];
  roleId: number;
  sendWelcomeEmail?: boolean;
}

export interface StaffListResponse {
  staff: Staff[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ============ Duplicate Detection Types ============

export interface DuplicateCheckRequest {
  firstName: string;
  lastName: string;
  email?: string;
  organizationId?: number;
}

export interface SEOAAAssignment {
  assignmentId: number;
  organizationId: number;
  organizationName: string;
  positionTitle?: string;
  roleName?: string;
}

export interface DuplicateMatch {
  personId: number;
  staffId: number;
  matchConfidence: number; // 0.0 to 1.0
  matchReasons: string[]; // ["exact_name_match", "email_match", etc.]
  firstName: string;
  middleName?: string;
  lastName: string;
  preferredName?: string;
  displayName: string;
  primaryEmail?: string;
  primaryPhone?: string;
  assignments: SEOAAAssignment[];
}

// ============ Atomic Staff Creation Types ============

export interface StaffCreateWithPersonData {
  // Required
  firstName: string;
  lastName: string;
  email: string;

  // Optional
  middleName?: string;
  preferredName?: string;
  honorific?: string;
  birthDate?: string;
  staffClassificationDescriptorId?: number;
  hireDate?: string;
  isActive?: boolean;
}

export interface StaffCreateWithPersonResponse {
  success: boolean;
  staffId: number;
  personId: number;
  person: {
    personId: number;
    firstName: string;
    middleName?: string;
    lastName: string;
    preferredName?: string;
    displayName: string;
    email: string;
  };
  staff: {
    staffId: number;
    personId: number;
    isActive: boolean;
    hireDate?: string;
  };
}

export const staffApi = {
  // List staff with pagination and filtering
  async list(params?: {
    page?: number;
    pageSize?: number;
    organizationId?: number;
    search?: string;
    isActive?: boolean;
    hasUserAccount?: boolean;
    roleTag?: string;
    includeAssignments?: boolean;
  }): Promise<StaffListResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page || 1).toString());
    queryParams.append('pageSize', (params?.pageSize || 50).toString());
    if (params?.organizationId) {
      queryParams.append('organizationId', params.organizationId.toString());
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.isActive !== undefined) {
      queryParams.append('isActive', params.isActive.toString());
    }
    if (params?.hasUserAccount !== undefined) {
      queryParams.append('hasUserAccount', params.hasUserAccount.toString());
    }
    if (params?.roleTag) {
      queryParams.append('roleTag', params.roleTag);
    }
    if (params?.includeAssignments !== undefined) {
      queryParams.append('includeAssignments', params.includeAssignments.toString());
    }

    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<StaffListResponse>(
      `/staff/?${queryParams}`
    );
  },

  // Get distinct role tags for staff filtering
  // Returns objects with codeValue (for filtering) and shortDescription (for display)
  async getRoleTags(): Promise<Array<{ codeValue: string; shortDescription: string }>> {
    return await apiFetch<Array<{ codeValue: string; shortDescription: string }>>('/staff/roleTags');
  },

  // Suggest role tags for a position title (best guess first, [] if none)
  async getRoleTagSuggestions(positionTitle: string, educationOrganizationId?: number): Promise<RoleTagSuggestion[]> {
    const queryParams = new URLSearchParams({ positionTitle });
    if (educationOrganizationId) {
      queryParams.append('educationOrganizationId', educationOrganizationId.toString());
    }
    return await apiFetch<RoleTagSuggestion[]>(`/staff/roleTagSuggestions?${queryParams}`);
  },

  // Get a single staff member by ID
  async get(staffId: number): Promise<Staff> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<Staff>(`/staff/${staffId}`);
  },

  // Create a new staff member
  async create(data: StaffCreateData): Promise<Staff> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<Staff>('/staff/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Update a staff member
  async update(staffId: number, data: StaffUpdateData): Promise<Staff> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<Staff>(`/staff/${staffId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Delete a staff member
  async delete(staffId: number): Promise<void> {
    // apiFetch already handles errors and returns parsed JSON
    await apiFetch<void>(`/staff/${staffId}`, {
      method: 'DELETE'
    });
  },

  // Add assignment to staff member
  async addAssignment(
    staffId: number,
    data: StaffAssignmentCreateData,
    force: boolean = false
  ): Promise<StaffAssignment> {
    const query = force ? '?force=true' : '';
    return await apiFetch<StaffAssignment>(
      `/staff/${staffId}/assignments${query}`,
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    );
  },

  // Update staff assignment
  async updateAssignment(
    staffId: number,
    assignmentId: number,
    data: StaffAssignmentUpdateData,
    force: boolean = false
  ): Promise<StaffAssignment> {
    const query = force ? '?force=true' : '';
    return await apiFetch<StaffAssignment>(
      `/staff/${staffId}/assignments/${assignmentId}${query}`,
      {
        method: 'PUT',
        body: JSON.stringify(data)
      }
    );
  },

  // Delete staff assignment
  async deleteAssignment(
    staffId: number,
    assignmentId: number
  ): Promise<void> {
    // apiFetch already handles errors and returns parsed JSON
    await apiFetch<void>(
      `/staff/${staffId}/assignments/${assignmentId}`,
      {
        method: 'DELETE'
      }
    );
  },

  // Bulk enable login for multiple staff members
  async bulkEnableLogin(
    data: BulkEnableLoginRequest
  ): Promise<{
    message: string;
    successCount: number;
    failedCount: number;
    userAccounts: Array<{
      staffId: number;
      userId: number;
      personName: string;
      email: string;
    }>;
  }> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<{
      message: string;
      successCount: number;
      failedCount: number;
      userAccounts: Array<{
        staffId: number;
        userId: number;
        personName: string;
        email: string;
      }>;
    }>('/staff/bulk-enable-login', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // ============ New Staff Creation Methods ============

  // Check for duplicate persons before creating staff
  async checkDuplicates(
    data: DuplicateCheckRequest
  ): Promise<DuplicateMatch[]> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<DuplicateMatch[]>('/staff/checkDuplicates', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Create person and staff records atomically
  async createWithPerson(
    data: StaffCreateWithPersonData
  ): Promise<StaffCreateWithPersonResponse> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<StaffCreateWithPersonResponse>(
      '/staff/createWithPerson',
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    );
  },

  // ============ Assignment Management ============

  // Update only the begin/end dates of an assignment (for deactivation workflows)
  // Pass null to explicitly clear a date (reactivate an assignment)
  async updateAssignmentDates(
    assignmentId: number,
    dates: { beginDate?: string | null; endDate?: string | null }
  ): Promise<{ success: boolean }> {
    return await apiFetch<{ success: boolean }>(
      `/staff/assignments/${assignmentId}/dates`,
      {
        method: 'PATCH',
        body: JSON.stringify(dates)
      }
    );
  },

  // End all active assignments for a staff member (bulk deactivation)
  async endAllAssignments(
    staffId: number,
    organizationIds?: number[]
  ): Promise<{ endedCount: number }> {
    return await apiFetch<{ endedCount: number }>(
      `/staff/${staffId}/assignments/endAll`,
      {
        method: 'POST',
        body: JSON.stringify({ organizationIds })
      }
    );
  }
};